/**
 * Demander la création d'un article, et suivre ses demandes.
 *
 * *Décision de Chris, 07/09/2026 : « il vaut mieux que [le magasinier] adresse une demande a
 * travaux pour la création d'un article et c'est elle qui le validera définitivement ».*
 * *Précisé le 08/09/2026 : la photo se prend dans la demande, et **n'importe qui** peut demander —
 * « cela pourrait être aussi une autre assistante administrative qui fait la demande ou bien
 * peut-être même un conducteur, on peut passer par le même chemin ».*
 *
 * ## Cet écran DEMANDE, il ne décide pas
 *
 * La validation vivait ici jusqu'au 08/09/2026, et c'était le mauvais endroit : l'assistante
 * référente n'a rien à faire dans l'écran Stocks du magasin, et elle n'avait aucun moyen
 * d'apprendre qu'une demande l'attendait sans y passer. Elle décide désormais depuis
 * **Soumissions**, là où elle regarde déjà ce qu'on lui demande de valider.
 *
 * Un seul endroit pour demander, un seul pour décider. Les garder tous les deux ici aurait fait
 * deux chemins vers la même écriture — ce que ce produit ferme partout ailleurs.
 *
 * ## La photo se prend AU MOMENT de la demande
 *
 * *« Comme le magasinier va envoyer la demande avec son téléphone il faudrait rajouter une prise
 * de photo directement dans la demande s'il le souhaite. »* C'est le seul instant où il a l'objet
 * dans les mains ; lui demander de revenir la déposer une fois l'article créé, c'est la photo
 * jamais prise.
 *
 * Elle part **après** la création de la demande — l'identifiant n'existe pas avant, il n'y a pas
 * d'autre ordre possible. Si l'envoi échoue, la demande est enregistrée quand même et l'écran le
 * dit : la ligne offre alors de reprendre la photo, plutôt que de faire ressaisir le tout.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import {
  chargerDemandesArticles,
  chargerSitesDeStockage,
  demanderUnArticle,
  deposerPhotoDemande,
  LIBELLES_ORIGINE_DEMANDE,
} from "./api";
import type {
  DemandeArticle,
  OrigineDemandeArticle,
  SiteDeStockage,
  TableauDemandesArticles,
} from "./api";

function formaterDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear())}`;
}

/** Un décimal sans les zéros inutiles : « 42.50000 » se lit « 42.5 ». */
function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

const LIBELLE_STATUT = {
  en_attente: "En attente",
  validee: "Validée",
  refusee: "Refusée",
} as const;

const ORIGINES: readonly { readonly code: OrigineDemandeArticle; readonly libelle: string }[] = [
  { code: "livraison_fournisseur", libelle: LIBELLES_ORIGINE_DEMANDE.livraison_fournisseur },
  { code: "retour_chantier", libelle: LIBELLES_ORIGINE_DEMANDE.retour_chantier },
];

export function DemandesArticles({
  onOuvrirArticle,
}: {
  /** Ouvrir la fiche de stock de l'article créé — le geste suivant du magasinier. */
  readonly onOuvrirArticle?: (articleId: string) => void;
}): JSX.Element {
  const [tableau, setTableau] = useState<TableauDemandesArticles | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  // La demande en cours de saisie. Pas de prix : c'est l'assistante qui le saisit (069).
  const [description, setDescription] = useState("");
  const [unite, setUnite] = useState("");
  const [quantite, setQuantite] = useState("");
  const [origine, setOrigine] = useState<OrigineDemandeArticle>("livraison_fournisseur");
  /**
   * Le magasin où le demandeur a rangé la marchandise (078).
   *
   * Exigé si et seulement si la demande porte une quantité : c'est la validation qui posera
   * l'entrée en stock, et un mouvement doit dire dans quel magasin il entre. Présélectionné
   * quand il n'y en a qu'un — il n'y a alors rien à choisir.
   */
  const [magasin, setMagasin] = useState("");
  const [sites, setSites] = useState<readonly SiteDeStockage[]>([]);
  const [fournisseur, setFournisseur] = useState("");
  const [remarque, setRemarque] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [ouvertFormulaire, setOuvertFormulaire] = useState(false);

  const recharger = useCallback((): void => {
    chargerDemandesArticles()
      .then((r) => {
        setTableau(r);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, []);

  useEffect(() => {
    recharger();
  }, [recharger]);

  /*
    Les magasins, chargés une fois.

    Séparément des demandes : la liste ne change pas quand on pose une demande, et la recharger à
    chaque fois ferait un appel de plus pour un contenu identique. Un échec laisse la liste vide,
    et le formulaire dit alors qu'aucun magasin n'est déclaré — ce qui est la bonne réponse dans
    les deux cas, l'installation à finir comme la panne de lecture.
  */
  useEffect(() => {
    chargerSitesDeStockage()
      .then((r) => {
        setSites(r.sites);
        if (r.sites.length === 1) setMagasin(r.sites[0]?.id ?? "");
      })
      .catch(() => {
        setSites([]);
      });
  }, []);

  const demander = (): void => {
    setOccupe("nouvelle");
    setErreur(null);
    setAvis(null);
    const q = quantite.trim();
    demanderUnArticle({
      description: description.trim(),
      uniteBase: unite.trim() === "" ? null : unite.trim(),
      quantite: q === "" ? null : q,
      // L'origine ne part QUE s'il y a une quantité : sans elle, le serveur la refuse — une
      // origine seule ne décrit rien. Le magasin suit exactement la même règle (078).
      origine: q === "" ? null : origine,
      siteId: q === "" ? null : magasin,
      fournisseurNom: fournisseur.trim() === "" ? null : fournisseur.trim(),
      remarque: remarque.trim() === "" ? null : remarque.trim(),
    })
      .then(async (r) => {
        /*
          La photo part APRÈS, et son échec ne perd pas la demande.

          L'identifiant n'existe pas avant la création : il n'y a pas d'autre ordre possible. Si
          l'envoi échoue — réseau coupé dans un rayonnage, fichier trop lourd —, la demande est
          enregistrée et la ligne offrira de reprendre la photo. Faire ressaisir le tout pour une
          image serait la pire réponse.
        */
        let alerteePhoto = "";
        if (photo !== null) {
          try {
            await deposerPhotoDemande(r.demande.id, photo);
          } catch (x: unknown) {
            alerteePhoto =
              " Mais la PHOTO n'est pas partie (" +
              (x instanceof Error ? x.message : String(x)) +
              ") — reprenez-la sur la ligne ci-dessous.";
          }
        }

        /*
          On dit si quelqu'un a été prévenu, et on ne le suppose pas.

          Sans titulaire nommé, la demande est bien enregistrée mais aucun courriel n'est parti.
          Annoncer « demande envoyée » dans ce cas laisserait attendre une réponse que personne
          n'a été invité à donner — le mode de panne d'un écran d'alerte qui se lit « tout va
          bien ».
        */
        setAvis(
          (r.destinatairePrevenu
            ? `Demande transmise à ${r.destinataireNom ?? "l'assistante"}.`
            : "Demande enregistrée, mais PERSONNE n'a été prévenu : la fonction " +
              "« Validation des créations d'articles » n'a pas de titulaire. Prévenez " +
              "l'administrateur, qui la nomme dans l'écran Membres.") + alerteePhoto,
        );
        setDescription("");
        setUnite("");
        setQuantite("");
        setFournisseur("");
        setRemarque("");
        setPhoto(null);
        setOuvertFormulaire(false);
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  const reprendreLaPhoto = (demande: DemandeArticle, fichier: File): void => {
    setOccupe(demande.id);
    setErreur(null);
    setAvis(null);
    deposerPhotoDemande(demande.id, fichier)
      .then(() => {
        setAvis("Photo enregistrée sur la demande.");
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  const demandes = tableau?.demandes ?? [];
  const enAttente = demandes.filter((d) => d.statut === "en_attente");

  return (
    <section className="demandes-articles">
      <h3>
        Créations d&apos;articles
        {enAttente.length > 0 && <span className="demandes-articles__compte">{enAttente.length}</span>}
      </h3>

      {/* Replié par défaut : sur un téléphone, le geste courant est le scan, et un formulaire
          déployé pousserait tout le reste hors de l'écran. */}
      <details
        className="demandes-articles__nouvelle"
        open={ouvertFormulaire}
        onToggle={(e) => {
          setOuvertFormulaire((e.currentTarget as HTMLDetailsElement).open);
        }}
      >
        <summary>Demander la création d&apos;un article</summary>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            demander();
          }}
        >
          <p className="etat">
            L&apos;article n&apos;existe pas encore : c&apos;est l&apos;assistante référente qui le
            crée, avec son code de l&apos;ERP et son prix. La quantité que vous saisissez ici{" "}
            <strong>entrera en stock</strong> dès qu&apos;elle aura validé — vous n&apos;aurez
            rien à ressaisir.
          </p>

          <label>
            Description
            <input
              value={description}
              placeholder="GRILLE AVALOIR FONTE 500x500"
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          </label>

          {/* LA PHOTO, prise ici et maintenant : c'est le seul moment où l'objet est dans les
              mains. `capture="environment"` ouvre l'appareil photo ARRIÈRE du téléphone. */}
          <label className="demandes-articles__photo">
            Photo de l&apos;article
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                setPhoto(e.target.files?.[0] ?? null);
              }}
            />
            <small>
              {photo === null
                ? "Facultative. Elle sera jointe à la fiche de l'article créé."
                : `« ${photo.name} » partira avec la demande.`}
            </small>
          </label>

          <label>
            Unité
            <input
              value={unite}
              placeholder="U, ML, T, M3…"
              onChange={(e) => {
                setUnite(e.target.value);
              }}
            />
          </label>

          <label>
            Quantité reçue
            <input
              inputMode="decimal"
              value={quantite}
              onChange={(e) => {
                setQuantite(e.target.value);
              }}
            />
            <small>
              À laisser vide si vous demandez un article que vous n&apos;avez pas encore reçu.
            </small>
          </label>

          {/* L'origine n'a de sens qu'avec une quantité : c'est elle qui devient le mouvement
              d'entrée, et un mouvement ne naît pas sans origine (066). */}
          {quantite.trim() !== "" && (
            <fieldset className="demandes-articles__origine">
              <legend>D&apos;où vient-elle ?</legend>
              {ORIGINES.map((o) => (
                <label key={o.code}>
                  <input
                    type="radio"
                    name="origine-demande"
                    checked={origine === o.code}
                    onChange={() => {
                      setOrigine(o.code);
                    }}
                  />
                  {o.libelle}
                </label>
              ))}
            </fieldset>
          )}

          {/*
            LE MAGASIN, sous la même condition que l'origine (078).

            C'est la validation qui posera l'entrée en stock, et un mouvement doit dire dans quel
            magasin il entre. Masqué quand il n'y en a qu'un : il est présélectionné, et une liste
            à une entrée est un clic pour rien.
          */}
          {quantite.trim() !== "" && sites.length > 1 && (
            <label>
              Rangée dans quel magasin ?
              <select
                value={magasin}
                onChange={(e) => {
                  setMagasin(e.target.value);
                }}
              >
                <option value="">— choisir —</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.denomination}
                  </option>
                ))}
              </select>
            </label>
          )}
          {quantite.trim() !== "" && sites.length === 0 && (
            <p className="alerte" role="alert">
              Aucun site de stockage n&apos;est déclaré : la quantité ne pourra pas entrer en
              stock. Demandez la création sans quantité, ou faites cocher un site de stockage au
              référentiel.
            </p>
          )}

          <label>
            Fournisseur
            <input
              value={fournisseur}
              onChange={(e) => {
                setFournisseur(e.target.value);
              }}
            />
          </label>

          <label>
            Remarque
            <input
              value={remarque}
              onChange={(e) => {
                setRemarque(e.target.value);
              }}
            />
          </label>

          <button type="submit" disabled={occupe !== null || description.trim() === ""}>
            {occupe === "nouvelle" ? "Envoi…" : "Demander la création"}
          </button>
        </form>
      </details>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      {avis !== null && <p className="etat">{avis}</p>}

      {tableau !== null && tableau.titulaire === null && (
        <p className="etat">
          Personne ne tient la fonction « Validation des créations d&apos;articles » : une demande
          s&apos;enregistrera, mais personne ne sera prévenu. L&apos;administrateur nomme un
          titulaire dans l&apos;écran Membres.
        </p>
      )}

      {tableau === null ? (
        <p className="etat">Chargement…</p>
      ) : demandes.length === 0 ? (
        <p className="etat">Aucune demande de création.</p>
      ) : (
        <ul className="demandes-articles__liste">
          {demandes.map((d) => (
            <Ligne
              key={d.id}
              demande={d}
              occupe={occupe === d.id}
              onPhoto={(fichier) => {
                reprendreLaPhoto(d, fichier);
              }}
              {...(onOuvrirArticle === undefined ? {} : { onOuvrirArticle })}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function Ligne({
  demande,
  occupe,
  onPhoto,
  onOuvrirArticle,
}: {
  readonly demande: DemandeArticle;
  readonly occupe: boolean;
  readonly onPhoto: (fichier: File) => void;
  readonly onOuvrirArticle?: (articleId: string) => void;
}): JSX.Element {
  const enAttente = demande.statut === "en_attente";

  return (
    <li className={`demandes-articles__ligne demandes-articles__ligne--${demande.statut}`}>
      <p className="demandes-articles__entete">
        <strong>{demande.description}</strong>
        <span className={enAttente ? "en-ecart" : undefined}>
          {LIBELLE_STATUT[demande.statut]}
        </span>
      </p>

      <p className="demandes-articles__details">
        {/* La quantité d'abord : c'est ce qui entrera en stock, donc ce qu'il faut relire. */}
        {demande.quantite !== null && (
          <span className="demandes-articles__quantite">
            {sansZeros(demande.quantite)} {demande.uniteBase ?? ""}
            {demande.origine !== null && ` · ${LIBELLES_ORIGINE_DEMANDE[demande.origine]}`}
          </span>
        )}
        {demande.quantite === null && demande.uniteBase !== null && (
          <span>Unité {demande.uniteBase}</span>
        )}
        {demande.fournisseurNom !== null && <span>{demande.fournisseurNom}</span>}
        {/* « Depuis quand » est la seule chose qui distingue une demande qu'on vient de poser
            d'une qu'on a oubliée. */}
        <span>
          Demandée le {formaterDate(demande.creeLe)}
          {demande.demandeurNom !== null && ` par ${demande.demandeurNom}`}
        </span>
      </p>

      {demande.remarque !== null && (
        <p className="demandes-articles__remarque">« {demande.remarque} »</p>
      )}

      {demande.photo !== null && (
        <img
          className="demandes-articles__vignette"
          src={demande.photo}
          alt={`Photo de ${demande.description}`}
        />
      )}

      {/* La photo se reprend tant que la demande attend : après validation, c'est la fiche
          article qui la porte et qui se corrige — le serveur refuse d'ailleurs le dépôt. */}
      {enAttente && (
        <label className="demandes-articles__prise">
          <span>
            {occupe
              ? "Envoi…"
              : demande.photo === null
                ? "Ajouter une photo"
                : "Remplacer la photo"}
          </span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={occupe}
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              if (fichier !== undefined) onPhoto(fichier);
              e.target.value = "";
            }}
          />
        </label>
      )}

      {demande.statut === "validee" && demande.article !== null && (
        <p className="demandes-articles__suite">
          Créé sous le code <strong>{demande.article.code}</strong>
          {demande.article.coutUnitaire !== null &&
            ` à ${sansZeros(demande.article.coutUnitaire)} €`}
          {demande.decideurNom !== null && ` par ${demande.decideurNom}`}
          {/* Ce qui est DÉJÀ entré en stock, dit en toutes lettres : sans ça le magasinier
              retournerait le saisir, et le stock compterait deux fois la même livraison. */}
          {demande.entreeEnStock !== null && (
            <>
              {" — "}
              <strong>
                {sansZeros(demande.entreeEnStock.quantite)} {demande.uniteBase ?? ""} déjà en
                stock
              </strong>{" "}
              ({demande.entreeEnStock.valeur} €), rien à ressaisir
            </>
          )}
          {onOuvrirArticle !== undefined && (
            <button
              type="button"
              className="lien"
              onClick={() => {
                onOuvrirArticle(demande.article?.id ?? "");
              }}
            >
              {demande.entreeEnStock === null
                ? "Ouvrir pour saisir l'entrée"
                : "Ouvrir la fiche de stock"}
            </button>
          )}
        </p>
      )}

      {demande.statut === "refusee" && (
        <p className="demandes-articles__suite alerte">
          Refusée{demande.decideurNom !== null && ` par ${demande.decideurNom}`} :{" "}
          {demande.motifRefus ?? ""}
        </p>
      )}
    </li>
  );
}
